/**
 * Animation Engine — Pure computation module for animation transforms.
 *
 * All functions are pure and lightweight — no DOM access, no side effects.
 * These are called per-item per-frame during preview playback.
 *
 * Computes transform values (position offset, scale, rotation, opacity, filter)
 * from an Animation_Config and a time value `t`.
 */

// ---------------------------------------------------------------------------
// Text animation type set
// ---------------------------------------------------------------------------

const TEXT_ANIMATION_TYPES = new Set([
  'typing',
  'marquee',
  'ascend',
  'shift',
  'merge',
  'block',
  'burst',
  'bounceText',
  'roll',
  'skate',
  'spread',
  'clarify',
  // Category 1 entrance — per-glyph reveals
  'typewriter',
  'wordReveal',
  'letterReveal',
  // Category 4 loop — per-character wave / letter-spacing loop
  'wave',
  'trackingLoop',
  // Category 5 — character & word presets
  'staggeredRise',
  'staggeredZoom',
  'charRotationCascade',
  'scatterAssemble',
  'wordHighlightCascade',
  // Category 6 — advanced reveals
  'trackingReveal',
  'charFlipCascade',
  'curtainReveal',
  'liquidReveal',
  'textDrawOn',
  'textBehindObject',
]);

// ---------------------------------------------------------------------------
// Easing functions
// ---------------------------------------------------------------------------

/**
 * Apply an easing function to a 0-1 progress value.
 * @param {number} progress - A value in [0, 1]
 * @param {string} easingType - One of 'linear', 'easeIn', 'easeOut', 'easeInOut'
 * @returns {number} The eased value in [0, 1]
 */
export const getEasingValue = (progress, easingType) => {
  const t = Math.max(0, Math.min(1, progress));

  switch (easingType) {
    case 'easeIn':
      return t * t * t;
    case 'easeOut':
      return 1 - Math.pow(1 - t, 3);
    case 'easeInOut':
      return t < 0.5
        ? 4 * t * t * t
        : 1 - Math.pow(-2 * t + 2, 3) / 2;
    case 'linear':
    default:
      return t;
  }
};

// ---------------------------------------------------------------------------
// Type guard
// ---------------------------------------------------------------------------

/**
 * Returns true if the given animation type is one of the 11 text animation types.
 * @param {string} animationType
 * @returns {boolean}
 */
export const isTextAnimation = (animationType) => {
  return TEXT_ANIMATION_TYPES.has(animationType);
};

// ---------------------------------------------------------------------------
// Timing helpers
// ---------------------------------------------------------------------------

/**
 * Compute clamped animation progress and apply easing.
 *
 * Timing model:
 *   Absolute animation start = itemTime.start + animConfig.start
 *   Absolute animation end   = itemTime.start + animConfig.start + animConfig.duration
 *   Clamped end              = min(absolute_end, itemTime.start + itemTime.duration)
 *   progress                 = clamp((t - absolute_start) / effective_duration, 0, 1)
 *
 * @param {object} animConfig - Animation configuration with `start`, `duration`, `easing`
 * @param {number} t - Current absolute time
 * @param {object} itemTime - Item timing with `start` and `duration`
 * @returns {{ progress: number, easedProgress: number }}
 */
export const computeAnimationProgress = (animConfig, t, itemTime) => {
  const animStart = Math.max(0, animConfig.start || 0);
  const animDuration = animConfig.duration || 0;

  const absoluteStart = (itemTime.start || 0) + animStart;
  const absoluteEnd = (itemTime.start || 0) + animStart + animDuration;
  const clampedEnd = Math.min(absoluteEnd, (itemTime.start || 0) + (itemTime.duration || 0));

  const effectiveDuration = clampedEnd - absoluteStart;

  if (effectiveDuration <= 0) {
    // Instant or invalid — treat as complete
    return { progress: 1, easedProgress: 1 };
  }

  const raw = (t - absoluteStart) / effectiveDuration;
  const progress = Math.max(0, Math.min(1, raw));
  const easedProgress = getEasingValue(progress, animConfig.easing || 'linear');

  return { progress, easedProgress };
};

// ---------------------------------------------------------------------------
// Identity transform constant
// ---------------------------------------------------------------------------

const IDENTITY_TRANSFORM = {
  offsetX: 0,
  offsetY: 0,
  scale: 1,
  rotation: 0,
  opacity: 1,
  filter: null,
};

// ---------------------------------------------------------------------------
// Basic animation transforms
// ---------------------------------------------------------------------------

/**
 * Compute basic animation transforms for text, shape, and image items.
 *
 * Supports: fade, motion, zoom, rotate, slide, bounce, pulse.
 * Unrecognized types return identity transforms with a console.warn.
 *
 * @param {object} animConfig - Animation configuration object
 * @param {number} t - Current absolute time
 * @param {object} itemTime - Item timing with `start` and `duration`
 * @returns {{ offsetX: number, offsetY: number, scale: number, rotation: number, opacity: number, filter: string|null }}
 */
export const computeAnimationTransform = (animConfig, t, itemTime) => {
  const { progress, easedProgress } = computeAnimationProgress(animConfig, t, itemTime);

  switch (animConfig.type) {
    case 'fade':
      return computeFade(animConfig, progress);
    case 'motion':
      return computeMotion(animConfig, easedProgress);
    case 'zoom':
      return computeZoom(animConfig, easedProgress);
    case 'rotate':
      return computeRotate(animConfig, easedProgress);
    case 'slide':
      return computeSlide(animConfig, easedProgress);
    case 'bounce':
      return computeBounce(animConfig, progress);
    case 'pulse':
      return computePulse(animConfig, progress);

    // ---- Category 1: Entrance animations (whole-item) ----
    case 'fadeIn':
      return computeFadeIn(animConfig, easedProgress);
    case 'riseUp':
      return computeRiseUp(animConfig, easedProgress);
    case 'slideIn':
      return computeSlideIn(animConfig, easedProgress);
    case 'zoomIn':
      return computeZoomIn(animConfig, easedProgress);
    case 'popIn':
      return computePopIn(animConfig, progress);
    case 'bounceIn':
      return computeBounceIn(animConfig, progress);
    case 'blurIn':
      return computeBlurIn(animConfig, easedProgress);
    case 'flipIn':
      return computeFlipIn(animConfig, easedProgress);
    case 'rotateIn':
      return computeRotateIn(animConfig, easedProgress);

    // ---- Category 2: Emphasis animations (whole-item, on visible text) ----
    case 'emphasisPulse':
      return computeEmphasisPulse(animConfig, progress);
    case 'shake':
      return computeShake(animConfig, progress);
    case 'emphasisBounce':
      return computeEmphasisBounce(animConfig, progress);
    case 'shimmer':
      return computeShimmer(animConfig, progress);
    case 'glitchLoop':
      return computeGlitchLoop(animConfig, progress);

    // ---- Category 3: Exit animations (whole-item) ----
    case 'fadeOut':
      return computeFadeOut(animConfig, easedProgress);
    case 'slideOut':
      return computeSlideOut(animConfig, easedProgress);
    case 'zoomOut':
      return computeZoomOut(animConfig, easedProgress);
    case 'blurOut':
      return computeBlurOut(animConfig, easedProgress);
    case 'dissolve':
      return computeDissolve(animConfig, easedProgress);

    // ---- Category 4: Loop animations (whole-item, repeat over the window) ----
    case 'floating':
      return computeFloating(animConfig, progress);
    case 'swing':
      return computeSwing(animConfig, progress);
    case 'breathing':
      return computeBreathing(animConfig, progress);

    default:
      // Text animation types are handled by computeTextAnimation, not here.
      // Only warn for truly unrecognized types.
      if (!isTextAnimation(animConfig.type)) {
        console.warn(`Unrecognized animation type: "${animConfig.type}"`);
      }
      return {
        offsetX: 0,
        offsetY: 0,
        scale: 1,
        rotation: 0,
        opacity: 1,
        filter: null,
      };
  }
};

// ---------------------------------------------------------------------------
// Fade
// ---------------------------------------------------------------------------

/**
 * Fade animation: opacity transitions through in → hold → out phases.
 * Uses raw progress for phase calculation, applies easing within each phase.
 *
 * @param {object} config - { in: number, out: number, easing?: string }
 * @param {number} progress - Raw (uneased) progress in [0, 1]
 * @returns {object} TransformResult
 */
const computeFade = (config, progress) => {
  const fadeIn = config.in || 0;
  const fadeOut = config.out || 0;
  const easing = config.easing || 'linear';

  let opacity;

  if (fadeIn + fadeOut > 1) {
    // If in + out exceed 1, scale them proportionally
    const total = fadeIn + fadeOut;
    const scaledIn = fadeIn / total;
    const scaledOut = fadeOut / total;

    if (progress <= scaledIn) {
      const phaseProgress = scaledIn > 0 ? progress / scaledIn : 1;
      opacity = getEasingValue(phaseProgress, easing);
    } else {
      const phaseProgress = scaledOut > 0 ? (progress - scaledIn) / scaledOut : 1;
      opacity = 1 - getEasingValue(phaseProgress, easing);
    }
  } else if (progress <= fadeIn) {
    // Fade-in phase
    const phaseProgress = fadeIn > 0 ? progress / fadeIn : 1;
    opacity = getEasingValue(phaseProgress, easing);
  } else if (progress >= 1 - fadeOut) {
    // Fade-out phase
    const phaseProgress = fadeOut > 0 ? (progress - (1 - fadeOut)) / fadeOut : 1;
    opacity = 1 - getEasingValue(phaseProgress, easing);
  } else {
    // Middle phase — full opacity
    opacity = 1;
  }

  return {
    offsetX: 0,
    offsetY: 0,
    scale: 1,
    rotation: 0,
    opacity: Math.max(0, Math.min(1, opacity)),
    filter: null,
  };
};

// ---------------------------------------------------------------------------
// Motion
// ---------------------------------------------------------------------------

/**
 * Motion animation: interpolate position, scale, and opacity from→to.
 *
 * @param {object} config - { from: { x, y, scale, opacity }, to: { x, y, scale, opacity } }
 * @param {number} ep - Eased progress in [0, 1]
 * @returns {object} TransformResult
 */
const computeMotion = (config, ep) => {
  const from = config.from || {};
  const to = config.to || {};

  const lerp = (a, b) => a + (b - a) * ep;

  return {
    offsetX: lerp(from.x || 0, to.x || 0),
    offsetY: lerp(from.y || 0, to.y || 0),
    scale: lerp(from.scale !== undefined ? from.scale : 1, to.scale !== undefined ? to.scale : 1),
    rotation: 0,
    opacity: Math.max(0, Math.min(1, lerp(
      from.opacity !== undefined ? from.opacity : 1,
      to.opacity !== undefined ? to.opacity : 1
    ))),
    filter: null,
  };
};

// ---------------------------------------------------------------------------
// Zoom
// ---------------------------------------------------------------------------

/**
 * Zoom animation: interpolate scale from→to.
 *
 * @param {object} config - { from: { scale }, to: { scale } }
 * @param {number} ep - Eased progress in [0, 1]
 * @returns {object} TransformResult
 */
const computeZoom = (config, ep) => {
  const fromScale = (config.from && config.from.scale !== undefined) ? config.from.scale : 1;
  const toScale = (config.to && config.to.scale !== undefined) ? config.to.scale : 1;

  return {
    offsetX: 0,
    offsetY: 0,
    scale: fromScale + (toScale - fromScale) * ep,
    rotation: 0,
    opacity: 1,
    filter: null,
  };
};

// ---------------------------------------------------------------------------
// Rotate
// ---------------------------------------------------------------------------

/**
 * Rotate animation: interpolate rotation from→to.
 *
 * @param {object} config - { from: { rotation }, to: { rotation } }
 * @param {number} ep - Eased progress in [0, 1]
 * @returns {object} TransformResult
 */
const computeRotate = (config, ep) => {
  const fromRot = (config.from && config.from.rotation !== undefined) ? config.from.rotation : 0;
  const toRot = (config.to && config.to.rotation !== undefined) ? config.to.rotation : 0;

  return {
    offsetX: 0,
    offsetY: 0,
    scale: 1,
    rotation: fromRot + (toRot - fromRot) * ep,
    opacity: 1,
    filter: null,
  };
};

// ---------------------------------------------------------------------------
// Slide
// ---------------------------------------------------------------------------

/**
 * Slide animation: translate from offset to origin based on direction.
 *
 * At progress=0: offset is `distance` pixels in the direction.
 * At progress=1: offset is 0 (item at original position).
 * Directions: left=-X, right=+X, up=-Y, down=+Y.
 *
 * @param {object} config - { direction, distance, easing }
 * @param {number} ep - Eased progress in [0, 1]
 * @returns {object} TransformResult
 */
const computeSlide = (config, ep) => {
  const distance = config.distance || 0;
  const remaining = 1 - ep; // fraction of offset remaining

  let offsetX = 0;
  let offsetY = 0;

  switch (config.direction) {
    case 'left':
      offsetX = -distance * remaining;
      break;
    case 'right':
      offsetX = distance * remaining;
      break;
    case 'up':
      offsetY = -distance * remaining;
      break;
    case 'down':
      offsetY = distance * remaining;
      break;
    default:
      // Default to left if direction is unrecognized
      offsetX = -distance * remaining;
      break;
  }

  return {
    offsetX,
    offsetY,
    scale: 1,
    rotation: 0,
    opacity: 1,
    filter: null,
  };
};

// ---------------------------------------------------------------------------
// Bounce
// ---------------------------------------------------------------------------

/**
 * Bounce animation: scale from 0, overshoot to intensity, oscillate, settle at 1.
 *
 * Uses a damped oscillation formula:
 *   envelope = 1 - (1 - progress)^2
 *   oscillation = cos(bounces * PI * progress)
 *   amplitude = (1 - progress) * (intensity - 1)
 *   scale = envelope + amplitude * oscillation
 *
 * The envelope is also used to gate the oscillation so that scale=0 at progress=0.
 *
 * At progress=0: scale=0, at progress=1: scale=1.
 *
 * @param {object} config - { intensity, bounces }
 * @param {number} progress - Raw progress in [0, 1]
 * @returns {object} TransformResult
 */
const computeBounce = (config, progress) => {
  const intensity = config.intensity || 1.2;
  const bounces = config.bounces || 3;

  // Envelope: fast rise, slow settle — goes from 0 to 1
  const envelope = 1 - (1 - progress) * (1 - progress);

  // Damped oscillation around the envelope
  const oscillation = Math.cos(bounces * Math.PI * progress);
  const amplitude = (1 - progress) * (intensity - 1);

  // Gate by envelope so scale=0 at progress=0
  const scale = Math.max(0, envelope * (1 + amplitude * oscillation));

  return {
    offsetX: 0,
    offsetY: 0,
    scale,
    rotation: 0,
    opacity: 1,
    filter: null,
  };
};

// ---------------------------------------------------------------------------
// Pulse
// ---------------------------------------------------------------------------

/**
 * Pulse animation: oscillate scale between minScale and maxScale.
 *
 * scale = midScale + amplitude * sin(2 * PI * pulses * progress)
 * where midScale = (minScale + maxScale) / 2
 *       amplitude = (maxScale - minScale) / 2
 *
 * @param {object} config - { minScale, maxScale, pulses }
 * @param {number} progress - Raw progress in [0, 1]
 * @returns {object} TransformResult
 */
const computePulse = (config, progress) => {
  const minScale = config.minScale !== undefined ? config.minScale : 0.9;
  const maxScale = config.maxScale !== undefined ? config.maxScale : 1.1;
  const pulses = config.pulses || 2;

  const midScale = (minScale + maxScale) / 2;
  const amplitude = (maxScale - minScale) / 2;

  const scale = midScale + amplitude * Math.sin(2 * Math.PI * pulses * progress);

  return {
    offsetX: 0,
    offsetY: 0,
    scale,
    rotation: 0,
    opacity: 1,
    filter: null,
  };
};

// ===========================================================================
// Category 1: Entrance animations (whole-item transforms)
//
// These transform the whole text box (opacity / position / scale / rotation /
// blur). They are consumed exactly like fade/zoom/slide, so no per-glyph work
// is needed. Each animates from a hidden/offset start state at progress 0 to
// the item's natural state at progress 1. The returned shape extends the base
// transform with optional scaleX/scaleY/rotationX/rotationY (for flip/stretch);
// consumers treat missing fields as identity.
// ===========================================================================

/**
 * Fade In: opacity ramps from `startOpacity` (default 0) to `endOpacity` (default 1).
 * Position/scale/rotation unchanged. Default duration 0.5s.
 * config: { startOpacity, endOpacity }
 */
const computeFadeIn = (config, ep) => {
  const startOpacity = config.startOpacity !== undefined ? config.startOpacity : 0;
  const endOpacity = config.endOpacity !== undefined ? config.endOpacity : 1;
  const opacity = startOpacity + (endOpacity - startOpacity) * ep;
  return {
    offsetX: 0, offsetY: 0, scale: 1, rotation: 0,
    opacity: Math.max(0, Math.min(1, opacity)),
    filter: null,
  };
};

/**
 * Rise Up: text travels from an offset position into place. Direction up/down/left/right.
 * Optionally fades in. Default distance 80px, duration 0.6s.
 * config: { direction:'up'|'down'|'left'|'right', distance, fade:boolean }
 */
const computeRiseUp = (config, ep) => {
  const distance = config.distance !== undefined ? config.distance : 80;
  const direction = config.direction || 'up';
  const remaining = 1 - ep;
  let offsetX = 0, offsetY = 0;
  switch (direction) {
    case 'down': offsetY = -distance * remaining; break;
    case 'left': offsetX = distance * remaining; break;
    case 'right': offsetX = -distance * remaining; break;
    case 'up':
    default: offsetY = distance * remaining; break; // starts below, rises up
  }
  const fade = config.fade !== undefined ? config.fade : true;
  return {
    offsetX, offsetY, scale: 1, rotation: 0,
    opacity: fade ? ep : 1,
    filter: null,
  };
};

/**
 * Slide In: directional entrance. Like Rise Up but defaults to horizontal and a
 * larger travel distance; can start fully outside the frame.
 * config: { direction:'left'|'right'|'top'|'bottom', distance, fade:boolean }
 */
const computeSlideIn = (config, ep) => {
  const distance = config.distance !== undefined ? config.distance : 200;
  const direction = config.direction || 'left';
  const remaining = 1 - ep;
  let offsetX = 0, offsetY = 0;
  switch (direction) {
    case 'right': offsetX = distance * remaining; break;
    case 'top': offsetY = -distance * remaining; break;
    case 'bottom': offsetY = distance * remaining; break;
    case 'left':
    default: offsetX = -distance * remaining; break;
  }
  const fade = config.fade !== undefined ? config.fade : false;
  return {
    offsetX, offsetY, scale: 1, rotation: 0,
    opacity: fade ? ep : 1,
    filter: null,
  };
};

/**
 * Zoom In: scale from `startScale` (default 0.5) to `endScale` (default 1). Optional fade.
 * config: { startScale, endScale, fade:boolean }
 */
const computeZoomIn = (config, ep) => {
  const startScale = config.startScale !== undefined ? config.startScale : 0.5;
  const endScale = config.endScale !== undefined ? config.endScale : 1;
  const fade = config.fade !== undefined ? config.fade : true;
  return {
    offsetX: 0, offsetY: 0,
    scale: startScale + (endScale - startScale) * ep,
    rotation: 0,
    opacity: fade ? ep : 1,
    filter: null,
  };
};

/**
 * Pop In: punchy 3-stage scale — small -> overshoot -> settle to final.
 * config: { startScale, overshoot, endScale, fade:boolean }. Default duration 0.35s.
 * Uses raw progress; overshoot peaks around 70% then settles.
 */
const computePopIn = (config, progress) => {
  const startScale = config.startScale !== undefined ? config.startScale : 0.2;
  const overshoot = config.overshoot !== undefined ? config.overshoot : 1.15;
  const endScale = config.endScale !== undefined ? config.endScale : 1;
  const fade = config.fade !== undefined ? config.fade : true;

  const peakAt = 0.7;
  let scale;
  if (progress <= peakAt) {
    // Rise from start to overshoot (ease-out)
    const p = peakAt > 0 ? progress / peakAt : 1;
    const eased = 1 - Math.pow(1 - p, 2);
    scale = startScale + (overshoot - startScale) * eased;
  } else {
    // Settle from overshoot to final
    const p = (progress - peakAt) / (1 - peakAt);
    const eased = 1 - Math.pow(1 - p, 2);
    scale = overshoot + (endScale - overshoot) * eased;
  }
  return {
    offsetX: 0, offsetY: 0, scale: Math.max(0, scale), rotation: 0,
    opacity: fade ? Math.min(1, progress / 0.3) : 1,
    filter: null,
  };
};

/**
 * Bounce In: elastic entrance. Scale (or position) overshoots and settles with
 * damped oscillations. config: { intensity, bounces, damping, mode:'scale'|'position',
 * direction, distance }. Default duration 0.8s.
 */
const computeBounceIn = (config, progress) => {
  const intensity = config.intensity !== undefined ? config.intensity : 1.2;
  const bounces = config.bounces !== undefined ? config.bounces : 3;
  const damping = config.damping !== undefined ? config.damping : 2;
  const mode = config.mode || 'scale';

  // Damped oscillation that settles at 1 (progress=1).
  const envelope = 1 - Math.pow(1 - progress, 2);
  const decay = Math.pow(1 - progress, damping);
  const oscillation = Math.cos(bounces * Math.PI * progress);
  const overshootAmt = (intensity - 1) * decay * oscillation;

  if (mode === 'position') {
    const distance = config.distance !== undefined ? config.distance : 80;
    const direction = config.direction || 'up';
    // Base travel toward target, with bounce overshoot near the end.
    const base = (1 - envelope) + overshootAmt;
    let offsetX = 0, offsetY = 0;
    switch (direction) {
      case 'down': offsetY = -distance * base; break;
      case 'left': offsetX = distance * base; break;
      case 'right': offsetX = -distance * base; break;
      case 'up':
      default: offsetY = distance * base; break;
    }
    return {
      offsetX, offsetY, scale: 1, rotation: 0,
      opacity: Math.min(1, progress / 0.2),
      filter: null,
    };
  }

  const scale = Math.max(0, envelope + overshootAmt);
  return {
    offsetX: 0, offsetY: 0, scale, rotation: 0,
    opacity: Math.min(1, progress / 0.2),
    filter: null,
  };
};

/**
 * Blur In: emerge from out-of-focus. Blur radius goes from `startBlur` (default 12px)
 * to `endBlur` (default 0). Optional fade. config: { startBlur, endBlur, fade:boolean }
 */
const computeBlurIn = (config, ep) => {
  const startBlur = config.startBlur !== undefined ? config.startBlur : 12;
  const endBlur = config.endBlur !== undefined ? config.endBlur : 0;
  const blur = startBlur + (endBlur - startBlur) * ep;
  const fade = config.fade !== undefined ? config.fade : true;
  return {
    offsetX: 0, offsetY: 0, scale: 1, rotation: 0,
    opacity: fade ? ep : 1,
    filter: `blur(${Math.max(0, blur).toFixed(2)}px)`,
  };
};

/**
 * Flip In: 3D flip around a horizontal or vertical axis.
 * config: { axis:'x'|'y', startAngle, fade:boolean }. Default startAngle 90 (edge-on).
 * Returns rotationX/rotationY (degrees) which consumers apply via 3D transform.
 */
const computeFlipIn = (config, ep) => {
  const axis = config.axis || 'x';
  const startAngle = config.startAngle !== undefined ? config.startAngle : 90;
  const angle = startAngle * (1 - ep);
  const fade = config.fade !== undefined ? config.fade : true;
  return {
    offsetX: 0, offsetY: 0, scale: 1, rotation: 0,
    rotationX: axis === 'x' ? angle : 0,
    rotationY: axis === 'y' ? angle : 0,
    opacity: fade ? Math.min(1, ep * 2) : 1,
    filter: null,
  };
};

/**
 * Rotate In: rotate from `startAngle` (default -180) to `endAngle` (default 0),
 * optionally with extra full turns. config: { startAngle, endAngle, turns, fade:boolean }
 */
const computeRotateIn = (config, ep) => {
  const startAngle = config.startAngle !== undefined ? config.startAngle : -180;
  const endAngle = config.endAngle !== undefined ? config.endAngle : 0;
  const turns = config.turns || 0;
  const total = (endAngle + turns * 360) - startAngle;
  const rotation = startAngle + total * ep;
  const fade = config.fade !== undefined ? config.fade : true;
  return {
    offsetX: 0, offsetY: 0, scale: 1,
    rotation,
    opacity: fade ? ep : 1,
    filter: null,
  };
};

// ===========================================================================
// Category 2: Emphasis animations (operate on already-visible text)
//
// These draw attention without the text having to disappear first. They start
// and end at the item's natural state (scale 1, offset 0, opacity 1) so they
// can be used on visible text. Shake/Glitch use a deterministic pseudo-random
// generator so the editor preview and the worker render match frame-for-frame.
// ===========================================================================

/**
 * Deterministic pseudo-random in [-1, 1] from an integer step and seed.
 * Must stay identical to the Python mirror so preview == render.
 */
const seededNoise = (step, seed) => {
  const x = Math.sin((step + 1) * 127.1 + seed * 311.7) * 43758.5453;
  const frac = x - Math.floor(x);
  return frac * 2 - 1;
};

/**
 * Pulse (emphasis): scale grows slightly above normal and returns, repeating
 * `pulseCount` times, settling exactly at 1. config: { minScale, maxScale, pulseCount }
 */
const computeEmphasisPulse = (config, progress) => {
  const minScale = config.minScale !== undefined ? config.minScale : 1;
  const maxScale = config.maxScale !== undefined ? config.maxScale : 1.15;
  const pulseCount = config.pulseCount !== undefined ? config.pulseCount : 2;

  // sin^2 keeps scale >= min and returns to min at the end of each cycle.
  const phase = Math.sin(Math.PI * pulseCount * progress);
  const scale = minScale + (maxScale - minScale) * (phase * phase);
  return {
    offsetX: 0, offsetY: 0, scale, rotation: 0, opacity: 1, filter: null,
  };
};

/**
 * Shake: rapid vibration around the current position, damped to zero at the end.
 * config: { strength (px), frequency (shakes), rotateAmount (deg), damping, seed,
 *           direction:'both'|'horizontal'|'vertical' }
 */
const computeShake = (config, progress) => {
  const strength = config.strength !== undefined ? config.strength : 8;
  const frequency = config.frequency !== undefined ? config.frequency : 12;
  const rotateAmount = config.rotateAmount !== undefined ? config.rotateAmount : 0;
  const damping = config.damping !== undefined ? config.damping : 1;
  const seed = config.seed !== undefined ? config.seed : 1;
  const direction = config.direction || 'both';

  const decay = Math.pow(1 - progress, damping); // -> 0 at end
  const step = Math.floor(progress * frequency * 2);
  const nx = seededNoise(step, seed);
  const ny = seededNoise(step, seed + 99);
  const nr = seededNoise(step, seed + 7);

  const offsetX = (direction === 'vertical') ? 0 : strength * nx * decay;
  const offsetY = (direction === 'horizontal') ? 0 : strength * ny * decay;
  const rotation = rotateAmount * nr * decay;

  return {
    offsetX, offsetY, scale: 1, rotation, opacity: 1, filter: null,
  };
};

/**
 * Bounce (emphasis): visible text bounces (vertical offset or scale) with
 * successively smaller bounces, settling at the origin.
 * config: { distance (px), bounces, damping, mode:'position'|'scale' }
 */
const computeEmphasisBounce = (config, progress) => {
  const distance = config.distance !== undefined ? config.distance : 24;
  const bounces = config.bounces !== undefined ? config.bounces : 3;
  const damping = config.damping !== undefined ? config.damping : 2;
  const mode = config.mode || 'position';

  const decay = Math.pow(1 - progress, damping);
  const osc = Math.abs(Math.sin(Math.PI * bounces * progress)); // bounces off the baseline
  const amount = distance * osc * decay;

  if (mode === 'scale') {
    return {
      offsetX: 0, offsetY: 0,
      scale: 1 + (amount / 100),
      rotation: 0, opacity: 1, filter: null,
    };
  }
  return {
    offsetX: 0, offsetY: -amount, scale: 1, rotation: 0, opacity: 1, filter: null,
  };
};

/**
 * Shimmer: a moving highlight sweeps across the text; the letters don't move.
 * Returns a `shimmer` descriptor the renderer uses to draw a bright gradient band.
 * config: { color, width (0-1 fraction of box), brightness, speed (sweeps),
 *           angle (deg), repeat:boolean }
 */
const computeShimmer = (config, progress) => {
  const sweeps = config.speed !== undefined ? config.speed : 1;
  const repeat = config.repeat !== undefined ? config.repeat : true;
  // position goes 0->1 across the box per sweep. When repeat is off it stops at 1.
  let pos = (progress * sweeps) % 1;
  if (!repeat && progress * sweeps >= 1) pos = 1;
  return {
    offsetX: 0, offsetY: 0, scale: 1, rotation: 0, opacity: 1, filter: null,
    shimmer: {
      position: pos,
      color: config.color || '#ffffff',
      width: config.width !== undefined ? config.width : 0.25,
      brightness: config.brightness !== undefined ? config.brightness : 0.8,
      angle: config.angle !== undefined ? config.angle : 20,
    },
  };
};

/**
 * Glitch Loop: stable most of the time with short bursts of displacement +
 * RGB channel separation + flicker. Deterministic via seed.
 * config: { bursts, burstDuration (0-1), intensity (px),
 *           rgbOffset (px), flicker (0-1), seed }
 */
const computeGlitchLoop = (config, progress) => {
  const bursts = config.bursts !== undefined ? config.bursts : 4;
  const burstDuration = config.burstDuration !== undefined ? config.burstDuration : 0.18;
  const intensity = config.intensity !== undefined ? config.intensity : 8;
  const rgbOffset = config.rgbOffset !== undefined ? config.rgbOffset : 4;
  const flicker = config.flicker !== undefined ? config.flicker : 0.3;
  const seed = config.seed !== undefined ? config.seed : 1;

  // Is the current time inside a burst window?
  const cyclePos = (progress * bursts) % 1; // 0..1 within each cycle
  const inBurst = cyclePos < burstDuration;

  if (!inBurst) {
    return {
      offsetX: 0, offsetY: 0, scale: 1, rotation: 0, opacity: 1, filter: null,
      glitch: null,
    };
  }

  const step = Math.floor(progress * bursts * 10);
  const nx = seededNoise(step, seed);
  const nf = (seededNoise(step, seed + 23) + 1) / 2; // 0..1
  const offsetX = intensity * nx;
  const opacity = 1 - flicker * nf;

  return {
    offsetX, offsetY: 0, scale: 1, rotation: 0, opacity: Math.max(0, opacity), filter: null,
    glitch: {
      rgbOffset: rgbOffset * (seededNoise(step, seed + 5)),
      active: true,
    },
  };
};

// ===========================================================================
// Category 3: Exit animations (whole-item)
//
// These animate visible text away. They go from the natural state at
// progress 0 to a hidden/offset state at progress 1, and are meant to run at
// the END of the item window (the picker sets `start` accordingly). They do
// NOT restore to full opacity afterward — once progress hits 1 the text stays
// in its exit (hidden) state for the remainder of the window.
// ===========================================================================

/**
 * Fade Out: opacity from `startOpacity` (default 1) to `endOpacity` (default 0).
 * config: { startOpacity, endOpacity }
 */
const computeFadeOut = (config, ep) => {
  const startOpacity = config.startOpacity !== undefined ? config.startOpacity : 1;
  const endOpacity = config.endOpacity !== undefined ? config.endOpacity : 0;
  const opacity = startOpacity + (endOpacity - startOpacity) * ep;
  return {
    offsetX: 0, offsetY: 0, scale: 1, rotation: 0,
    opacity: Math.max(0, Math.min(1, opacity)),
    filter: null,
  };
};

/**
 * Slide Out: text travels away in `direction` by `distance` (default 200px).
 * At progress 0 it's at rest; at 1 it's fully displaced. Optional fade.
 * config: { direction:'left'|'right'|'up'|'down', distance, fade:boolean }
 */
const computeSlideOut = (config, ep) => {
  const distance = config.distance !== undefined ? config.distance : 200;
  const direction = config.direction || 'left';
  let offsetX = 0, offsetY = 0;
  switch (direction) {
    case 'right': offsetX = distance * ep; break;
    case 'up': offsetY = -distance * ep; break;
    case 'down': offsetY = distance * ep; break;
    case 'left':
    default: offsetX = -distance * ep; break;
  }
  const fade = config.fade !== undefined ? config.fade : false;
  return {
    offsetX, offsetY, scale: 1, rotation: 0,
    opacity: fade ? (1 - ep) : 1,
    filter: null,
  };
};

/**
 * Zoom Out: scale from `startScale` (default 1) to `endScale` (default 0). Optional fade.
 * config: { startScale, endScale, fade:boolean }
 */
const computeZoomOut = (config, ep) => {
  const startScale = config.startScale !== undefined ? config.startScale : 1;
  const endScale = config.endScale !== undefined ? config.endScale : 0;
  const fade = config.fade !== undefined ? config.fade : true;
  return {
    offsetX: 0, offsetY: 0,
    scale: Math.max(0, startScale + (endScale - startScale) * ep),
    rotation: 0,
    opacity: fade ? (1 - ep) : 1,
    filter: null,
  };
};

/**
 * Blur Out: blur radius from `startBlur` (default 0) to `endBlur` (default 12px).
 * Fades out too (blur alone doesn't make text disappear). config: { endBlur, startBlur, fade }
 */
const computeBlurOut = (config, ep) => {
  const startBlur = config.startBlur !== undefined ? config.startBlur : 0;
  const endBlur = config.endBlur !== undefined ? config.endBlur : 12;
  const blur = startBlur + (endBlur - startBlur) * ep;
  const fade = config.fade !== undefined ? config.fade : true;
  return {
    offsetX: 0, offsetY: 0, scale: 1, rotation: 0,
    opacity: fade ? (1 - ep) : 1,
    filter: `blur(${Math.max(0, blur).toFixed(2)}px)`,
  };
};

/**
 * Dissolve / Particle Exit: text breaks apart / dissolves into the background.
 * Emits a `dissolve` descriptor (amount 0->1) for the renderer's noise mask.
 * The whole-item opacity also falls off as a baseline so it works everywhere.
 * config: { falloff (opacity power, default 1), scatter (px drift, default 0) }
 */
const computeDissolve = (config, ep) => {
  const falloff = config.falloff !== undefined ? config.falloff : 1;
  const scatter = config.scatter !== undefined ? config.scatter : 0;
  const opacity = Math.max(0, 1 - Math.pow(ep, falloff));
  return {
    offsetX: 0, offsetY: scatter * ep, scale: 1, rotation: 0,
    opacity,
    filter: null,
    dissolve: { amount: ep, scatter },
  };
};

// ===========================================================================
// Category 4: Loop animations (whole-item, repeat continuously)
//
// These oscillate with a smooth periodic function over the window. `cycles`
// (whole number of oscillations across the window) keeps the motion continuous
// at the loop boundary so there is no jump when a looping video restarts.
// ===========================================================================

/**
 * Floating: gentle drift up/down (or left/right) using a sine oscillation.
 * config: { amplitude (px, default 10), cycles (default 2), axis:'vertical'|'horizontal',
 *           rotate (deg, small sway), phase (0-1) }
 */
const computeFloating = (config, progress) => {
  const amplitude = config.amplitude !== undefined ? config.amplitude : 10;
  const cycles = config.cycles !== undefined ? config.cycles : 2;
  const axis = config.axis || 'vertical';
  const rotate = config.rotate !== undefined ? config.rotate : 0;
  const phase = config.phase !== undefined ? config.phase : 0;

  const theta = 2 * Math.PI * (cycles * progress + phase);
  const s = Math.sin(theta);
  return {
    offsetX: axis === 'horizontal' ? amplitude * s : 0,
    offsetY: axis === 'horizontal' ? 0 : amplitude * s,
    scale: 1,
    rotation: rotate * Math.sin(theta),
    opacity: 1,
    filter: null,
  };
};

/**
 * Swing: rotate alternately to either side of rest, like a hanging sign.
 * config: { angle (deg, default 8), cycles (default 3), damping (0 = none) }
 * With damping > 0 the swing settles over the window; with 0 it loops evenly.
 */
const computeSwing = (config, progress) => {
  const angle = config.angle !== undefined ? config.angle : 8;
  const cycles = config.cycles !== undefined ? config.cycles : 3;
  const damping = config.damping !== undefined ? config.damping : 0;

  const decay = damping > 0 ? Math.pow(1 - progress, damping) : 1;
  const rotation = angle * Math.sin(2 * Math.PI * cycles * progress) * decay;
  return {
    offsetX: 0, offsetY: 0, scale: 1, rotation, opacity: 1, filter: null,
  };
};

/**
 * Breathing: slow, smooth scale between 1 and `maxScale`, with optional opacity
 * variation. config: { maxScale (default 1.03), cycles (default 1), opacityVar (0-1) }
 */
const computeBreathing = (config, progress) => {
  const maxScale = config.maxScale !== undefined ? config.maxScale : 1.03;
  const minScale = config.minScale !== undefined ? config.minScale : 1;
  const cycles = config.cycles !== undefined ? config.cycles : 1;
  const opacityVar = config.opacityVar !== undefined ? config.opacityVar : 0;

  // (1 - cos)/2 goes 0->1->0 smoothly, so scale breathes min->max->min.
  const wave = (1 - Math.cos(2 * Math.PI * cycles * progress)) / 2;
  const scale = minScale + (maxScale - minScale) * wave;
  const opacity = 1 - opacityVar * wave;
  return {
    offsetX: 0, offsetY: 0, scale, rotation: 0,
    opacity: Math.max(0, Math.min(1, opacity)),
    filter: null,
  };
};

// ---------------------------------------------------------------------------
// Text animation helpers
// ---------------------------------------------------------------------------

/** No-op text result — returned when animation is not a text type or text is empty. */
const NO_OP_TEXT_RESULT = {
  type: 'full-text',
  visibleCount: 0,
  showCursor: false,
  transforms: null,
  letterSpacing: null,
  filter: null,
};

/**
 * Compute per-unit eased progress for staggered text animations.
 *
 * Each unit (character / word / line) starts its individual animation at a
 * staggered offset.  The model:
 *   - elapsedTime   = overallProgress * effectiveDuration  (seconds)
 *   - unitDuration  = max(0.1, effectiveDuration - (N - 1) * stagger)
 *   - unitProgress  = clamp((elapsedTime - i * stagger) / unitDuration, 0, 1)
 *   - unitEased     = getEasingValue(unitProgress, easing)
 *
 * @param {number} overallProgress - Raw progress in [0, 1]
 * @param {number} effectiveDuration - Effective animation duration in seconds
 * @param {number} unitIndex - Index of the current unit (0-based)
 * @param {number} totalUnits - Total number of units
 * @param {number} stagger - Stagger delay between units in seconds
 * @param {string} easing - Easing type
 * @returns {{ unitProgress: number, unitEased: number }}
 */
const computeUnitProgress = (overallProgress, effectiveDuration, unitIndex, totalUnits, stagger, easing) => {
  const elapsed = overallProgress * effectiveDuration;
  const unitDuration = Math.max(0.1, effectiveDuration - (totalUnits - 1) * stagger);
  const raw = (elapsed - unitIndex * stagger) / unitDuration;
  const unitProgress = Math.max(0, Math.min(1, raw));
  const unitEased = getEasingValue(unitProgress, easing);
  return { unitProgress, unitEased };
};

/**
 * Build a per-character transforms array using a callback.
 *
 * @param {number} n - Number of characters
 * @param {number} progress - Raw overall progress
 * @param {number} duration - Effective duration in seconds
 * @param {number} stagger - Stagger in seconds
 * @param {string} easing - Easing type
 * @param {(unitProgress: number, unitEased: number, index: number) => object} fn
 *   Callback that returns { offsetX, offsetY, scale, rotation, opacity } for one unit.
 * @returns {object[]} Array of CharTransform objects
 */
const buildCharTransforms = (n, progress, duration, stagger, easing, fn) => {
  const transforms = new Array(n);
  for (let i = 0; i < n; i++) {
    const { unitProgress, unitEased } = computeUnitProgress(progress, duration, i, n, stagger, easing);
    transforms[i] = fn(unitProgress, unitEased, i);
  }
  return transforms;
};

// ---------------------------------------------------------------------------
// Text animation computations
// ---------------------------------------------------------------------------

/**
 * Compute text-specific animation state (text items only).
 *
 * Supports text animation types: typing, marquee, ascend, shift, merge,
 * block, burst, bounceText, roll, skate, spread, clarify.
 *
 * @param {object} animConfig - Animation configuration object
 * @param {number} t - Current absolute time
 * @param {object} itemTime - Item timing with `start` and `duration`
 * @param {number} textLength - Number of characters in the text
 * @returns {{ type: string, visibleCount: number|null, showCursor: boolean, transforms: object[]|null, letterSpacing: number|null, filter: string|null }}
 */
export const computeTextAnimation = (animConfig, t, itemTime, textLength, text) => {
  if (!textLength || textLength <= 0) {
    return { ...NO_OP_TEXT_RESULT, visibleCount: 0 };
  }

  const { progress, easedProgress } = computeAnimationProgress(animConfig, t, itemTime);

  // Effective duration in seconds (used for stagger calculations)
  const animStart = Math.max(0, animConfig.start || 0);
  const animDuration = animConfig.duration || 0;
  const absoluteStart = (itemTime.start || 0) + animStart;
  const absoluteEnd = (itemTime.start || 0) + animStart + animDuration;
  const clampedEnd = Math.min(absoluteEnd, (itemTime.start || 0) + (itemTime.duration || 0));
  const effectiveDuration = Math.max(0.01, clampedEnd - absoluteStart);

  const easing = animConfig.easing || 'linear';

  switch (animConfig.type) {
    case 'typing':
      return computeTyping(animConfig, progress, effectiveDuration, textLength);

    case 'marquee':
      return computeMarquee(animConfig, progress, effectiveDuration, textLength);

    case 'ascend':
      return computeAscend(animConfig, progress, effectiveDuration, textLength, easing);

    case 'shift':
      return computeShift(animConfig, progress, effectiveDuration, textLength, easing);

    case 'merge':
      return computeMerge(animConfig, progress, effectiveDuration, textLength, easing);

    case 'block':
      return computeBlock(animConfig, progress, effectiveDuration, textLength, easing);

    case 'burst':
      return computeBurst(animConfig, progress, effectiveDuration, textLength, easing);

    case 'bounceText':
      return computeBounceText(animConfig, progress, effectiveDuration, textLength, easing);

    case 'roll':
      return computeRoll(animConfig, progress, effectiveDuration, textLength, easing);

    case 'skate':
      return computeSkate(animConfig, progress, effectiveDuration, textLength, easing);

    case 'spread':
      return computeSpread(animConfig, easedProgress, textLength);

    case 'clarify':
      return computeClarify(animConfig, easedProgress);

    // ---- Category 1 entrance: per-glyph reveals ----
    case 'typewriter':
      return computeTypewriter(animConfig, progress, effectiveDuration, textLength);

    case 'wordReveal':
      return computeWordReveal(animConfig, progress, effectiveDuration, textLength);

    case 'letterReveal':
      return computeLetterReveal(animConfig, progress, effectiveDuration, textLength, easing);

    // ---- Category 4 loop: per-character wave / letter-spacing loop ----
    case 'wave':
      return computeWave(animConfig, progress, textLength);

    case 'trackingLoop':
      return computeTrackingLoop(animConfig, progress);

    // ---- Category 5: character & word presets ----
    case 'staggeredRise':
      return computeStaggeredRise(animConfig, progress, effectiveDuration, textLength, easing);

    case 'staggeredZoom':
      return computeStaggeredZoom(animConfig, progress, effectiveDuration, textLength, easing);

    case 'charRotationCascade':
      return computeCharRotationCascade(animConfig, progress, effectiveDuration, textLength, easing);

    case 'scatterAssemble':
      return computeScatterAssemble(animConfig, progress, effectiveDuration, textLength, easing);

    case 'wordHighlightCascade':
      return computeWordHighlightCascade(animConfig, progress, effectiveDuration, textLength, easing, text);

    // ---- Category 6: advanced reveals ----
    case 'trackingReveal':
      return computeTrackingReveal(animConfig, progress, effectiveDuration, textLength, easing);

    case 'charFlipCascade':
      return computeCharFlipCascade(animConfig, progress, effectiveDuration, textLength, easing);

    case 'curtainReveal':
      return computeCurtainReveal(animConfig, easedProgress);

    case 'liquidReveal':
      return computeLiquidReveal(animConfig, easedProgress);

    case 'textDrawOn':
      return computeTextDrawOn(animConfig, easedProgress);

    case 'textBehindObject':
      return computeTextBehindObject(animConfig, easedProgress);

    default:
      return {
        type: 'full-text',
        visibleCount: textLength,
        showCursor: false,
        transforms: null,
        letterSpacing: null,
        filter: null,
      };
  }
};

// ---------------------------------------------------------------------------
// Typing
// ---------------------------------------------------------------------------

/**
 * Typing: reveal characters one at a time based on speed (ms per char).
 * The last visible character gets a "pop" scale bounce effect.
 *
 * @param {object} config - { speed: 50, showCursor: true }
 * @param {number} progress - Raw progress [0, 1]
 * @param {number} duration - Effective duration in seconds
 * @param {number} textLength - Total characters
 * @returns {object} TextTransformResult
 */
const computeTyping = (config, progress, duration, textLength) => {
  const speed = config.speed || 50; // ms per character
  const elapsedMs = progress * duration * 1000;
  const visibleCount = Math.min(textLength, Math.floor(elapsedMs / speed));

  // Pop effect: compute scale for the last visible character
  let lastCharScale = 1;
  if (visibleCount > 0 && visibleCount <= textLength) {
    const charAppearedAtMs = (visibleCount - 1) * speed;
    const timeSinceAppear = elapsedMs - charAppearedAtMs;
    const popDuration = 300; // ms
    if (timeSinceAppear < popDuration) {
      const popProgress = timeSinceAppear / popDuration;
      // Ease-out bounce: starts at 2x, settles to 1x
      lastCharScale = 1 + 1.0 * Math.pow(1 - popProgress, 2);
    }
  }

  return {
    type: 'full-text',
    visibleCount,
    showCursor: config.showCursor !== undefined ? config.showCursor : true,
    lastCharScale,
    transforms: null,
    letterSpacing: null,
    filter: null,
  };
};

// ---------------------------------------------------------------------------
// Marquee
// ---------------------------------------------------------------------------

/**
 * Marquee: scroll text horizontally in a continuous loop within its bounding box.
 * Text scrolls continuously — when it exits one side, it re-enters from the other.
 * For seamless looping, the text is conceptually duplicated. The offset cycles
 * through the full text width so the loop is seamless.
 *
 * The renderer receives `marqueeOffset` (pixels) and `marqueeLoop: true` to know
 * it should render the text twice for seamless wrapping.
 *
 * @param {object} config - { speed: 100, direction: 'left' }
 * @param {number} progress - Raw progress [0, 1]
 * @param {number} duration - Effective duration in seconds
 * @param {number} textLength - Total characters
 * @returns {object} TextTransformResult
 */
const computeMarquee = (config, progress, duration, textLength) => {
  const speed = config.speed || 100; // pixels per second
  const direction = config.direction || 'left';
  // gap between the two copies of text (pixels)
  const gap = config.gap || 60;

  // Total distance the text travels per loop cycle.
  // We don't know the actual pixel width here (that's the renderer's job),
  // so we output a normalized phase (0-1) that the renderer maps to its
  // measured text width. We also output the raw pixel offset for renderers
  // that prefer it.
  const elapsedPx = progress * duration * speed;

  return {
    type: 'full-text',
    visibleCount: null,
    showCursor: false,
    transforms: null,
    letterSpacing: null,
    filter: null,
    marqueeOffset: elapsedPx,  // raw pixels traveled (always positive)
    marqueeDirection: direction,
    marqueeGap: gap,
    marqueeLoop: true,
  };
};

// ---------------------------------------------------------------------------
// Ascend
// ---------------------------------------------------------------------------

/**
 * Ascend: each character rises from below by `distance` pixels, staggered.
 *
 * @param {object} config - { distance: 50, stagger: 0.1 }
 * @param {number} progress - Raw progress [0, 1]
 * @param {number} duration - Effective duration in seconds
 * @param {number} textLength - Total characters
 * @param {string} easing - Easing type
 * @returns {object} TextTransformResult
 */
const computeAscend = (config, progress, duration, textLength, easing) => {
  const distance = config.distance || 50;
  const stagger = config.stagger || 0.1;

  const transforms = buildCharTransforms(textLength, progress, duration, stagger, easing,
    (_up, ue) => ({
      offsetX: 0,
      offsetY: distance * (1 - ue),
      scale: 1,
      rotation: 0,
      opacity: ue,
    })
  );

  return {
    type: 'per-character',
    visibleCount: null,
    showCursor: false,
    transforms,
    letterSpacing: null,
    filter: null,
  };
};

// ---------------------------------------------------------------------------
// Shift
// ---------------------------------------------------------------------------

/**
 * Shift: each character slides in from `direction` by `distance`, staggered.
 *
 * @param {object} config - { direction: 'horizontal'|'vertical', distance: 30, stagger: 0.05 }
 * @param {number} progress - Raw progress [0, 1]
 * @param {number} duration - Effective duration in seconds
 * @param {number} textLength - Total characters
 * @param {string} easing - Easing type
 * @returns {object} TextTransformResult
 */
const computeShift = (config, progress, duration, textLength, easing) => {
  const distance = config.distance || 30;
  const stagger = config.stagger || 0.05;
  const isHorizontal = config.direction !== 'vertical';

  const transforms = buildCharTransforms(textLength, progress, duration, stagger, easing,
    (_up, ue) => ({
      offsetX: isHorizontal ? distance * (1 - ue) : 0,
      offsetY: isHorizontal ? 0 : distance * (1 - ue),
      scale: 1,
      rotation: 0,
      opacity: ue,
    })
  );

  return {
    type: 'per-character',
    visibleCount: null,
    showCursor: false,
    transforms,
    letterSpacing: null,
    filter: null,
  };
};

// ---------------------------------------------------------------------------
// Merge
// ---------------------------------------------------------------------------

/**
 * Merge: characters converge from scattered positions. Even-index chars come
 * from the left, odd-index from the right.
 *
 * @param {object} config - { direction: 'center', distance: 100, stagger: 0.08 }
 * @param {number} progress - Raw progress [0, 1]
 * @param {number} duration - Effective duration in seconds
 * @param {number} textLength - Total characters
 * @param {string} easing - Easing type
 * @returns {object} TextTransformResult
 */
const computeMerge = (config, progress, duration, textLength, easing) => {
  const distance = config.distance || 100;
  const stagger = config.stagger || 0.08;

  const transforms = buildCharTransforms(textLength, progress, duration, stagger, easing,
    (_up, ue, i) => ({
      offsetX: (i % 2 === 0 ? -distance : distance) * (1 - ue),
      offsetY: 0,
      scale: 1,
      rotation: 0,
      opacity: ue,
    })
  );

  return {
    type: 'per-character',
    visibleCount: null,
    showCursor: false,
    transforms,
    letterSpacing: null,
    filter: null,
  };
};

// ---------------------------------------------------------------------------
// Block
// ---------------------------------------------------------------------------

/**
 * Block: reveal text in word or line blocks based on progress.
 *
 * @param {object} config - { blockSize: 'word'|'line', stagger: 0.2 }
 * @param {number} progress - Raw progress [0, 1]
 * @param {number} duration - Effective duration in seconds
 * @param {number} textLength - Total characters (used to estimate block count)
 * @param {string} easing - Easing type
 * @returns {object} TextTransformResult
 */
const computeBlock = (config, progress, duration, textLength, easing) => {
  const blockSize = config.blockSize || 'word';
  // We don't have the actual text here, so estimate block count.
  // For words: average ~5 chars per word. For lines: average ~40 chars per line.
  const avgBlockLen = blockSize === 'line' ? 40 : 5;
  const totalBlocks = Math.max(1, Math.ceil(textLength / avgBlockLen));

  const easedProgress = getEasingValue(progress, easing);
  const visibleCount = Math.min(totalBlocks, Math.floor(easedProgress * (totalBlocks + 1)));

  return {
    type: blockSize === 'line' ? 'per-line' : 'per-word',
    visibleCount,
    showCursor: false,
    transforms: null,
    letterSpacing: null,
    filter: null,
  };
};

// ---------------------------------------------------------------------------
// Burst
// ---------------------------------------------------------------------------

/**
 * Burst: each character scales from 0 → intensity → 1, staggered.
 * First half: scale ramps up to intensity. Second half: settles to 1.
 * Opacity fades in during the first half.
 *
 * @param {object} config - { intensity: 1.5, stagger: 0.03 }
 * @param {number} progress - Raw progress [0, 1]
 * @param {number} duration - Effective duration in seconds
 * @param {number} textLength - Total characters
 * @param {string} easing - Easing type
 * @returns {object} TextTransformResult
 */
const computeBurst = (config, progress, duration, textLength, easing) => {
  const intensity = config.intensity || 1.5;
  const stagger = config.stagger || 0.03;

  const transforms = buildCharTransforms(textLength, progress, duration, stagger, easing,
    (_up, ue) => {
      let scale;
      if (ue < 0.5) {
        // Ramp up: 0 → intensity
        scale = ue * 2 * intensity;
      } else {
        // Settle down: intensity → 1
        scale = intensity - (intensity - 1) * (ue - 0.5) * 2;
      }
      return {
        offsetX: 0,
        offsetY: 0,
        scale: Math.max(0, scale),
        rotation: 0,
        opacity: Math.min(1, ue * 2),
      };
    }
  );

  return {
    type: 'per-character',
    visibleCount: null,
    showCursor: false,
    transforms,
    letterSpacing: null,
    filter: null,
  };
};

// ---------------------------------------------------------------------------
// BounceText
// ---------------------------------------------------------------------------

/**
 * BounceText: each character drops from above by `height` pixels with a
 * damped bounce, staggered.
 *
 * @param {object} config - { height: 20, stagger: 0.1 }
 * @param {number} progress - Raw progress [0, 1]
 * @param {number} duration - Effective duration in seconds
 * @param {number} textLength - Total characters
 * @param {string} easing - Easing type
 * @returns {object} TextTransformResult
 */
const computeBounceText = (config, progress, duration, textLength, easing) => {
  const height = config.height || 20;
  const stagger = config.stagger || 0.1;

  const transforms = buildCharTransforms(textLength, progress, duration, stagger, easing,
    (up, ue) => ({
      offsetX: 0,
      offsetY: -height * (1 - ue),
      scale: 1,
      rotation: 0,
      opacity: ue,
    })
  );

  return {
    type: 'per-character',
    visibleCount: null,
    showCursor: false,
    transforms,
    letterSpacing: null,
    filter: null,
  };
};

// ---------------------------------------------------------------------------
// Roll
// ---------------------------------------------------------------------------

/**
 * Roll: each character rotates from an offset (360° or -360°) to 0°, staggered.
 *
 * @param {object} config - { direction: 'clockwise'|'counterclockwise', stagger: 0.1 }
 * @param {number} progress - Raw progress [0, 1]
 * @param {number} duration - Effective duration in seconds
 * @param {number} textLength - Total characters
 * @param {string} easing - Easing type
 * @returns {object} TextTransformResult
 */
const computeRoll = (config, progress, duration, textLength, easing) => {
  const stagger = config.stagger || 0.1;
  const fullRotation = config.direction === 'counterclockwise' ? -360 : 360;

  const transforms = buildCharTransforms(textLength, progress, duration, stagger, easing,
    (_up, ue) => ({
      offsetX: 0,
      offsetY: 0,
      scale: 1,
      rotation: fullRotation * (1 - ue),
      opacity: ue,
    })
  );

  return {
    type: 'per-character',
    visibleCount: null,
    showCursor: false,
    transforms,
    letterSpacing: null,
    filter: null,
  };
};

// ---------------------------------------------------------------------------
// Skate
// ---------------------------------------------------------------------------

/**
 * Skate: each character slides horizontally by `distance` with a vertical
 * `wobble` oscillation, staggered.
 *
 * @param {object} config - { distance: 40, wobble: 10, stagger: 0.05 }
 * @param {number} progress - Raw progress [0, 1]
 * @param {number} duration - Effective duration in seconds
 * @param {number} textLength - Total characters
 * @param {string} easing - Easing type
 * @returns {object} TextTransformResult
 */
const computeSkate = (config, progress, duration, textLength, easing) => {
  const distance = config.distance || 40;
  const wobble = config.wobble || 10;
  const stagger = config.stagger || 0.05;

  const transforms = buildCharTransforms(textLength, progress, duration, stagger, easing,
    (up, ue) => ({
      offsetX: distance * (1 - ue),
      offsetY: wobble * Math.sin(4 * Math.PI * up) * (1 - ue),
      scale: 1,
      rotation: 0,
      opacity: ue,
    })
  );

  return {
    type: 'per-character',
    visibleCount: null,
    showCursor: false,
    transforms,
    letterSpacing: null,
    filter: null,
  };
};

// ---------------------------------------------------------------------------
// Spread
// ---------------------------------------------------------------------------

/**
 * Spread: animate letter spacing from expanded to normal.
 * Uses overall eased progress (uniform spacing, not per-character).
 *
 * @param {object} config - { distance: 20 }
 * @param {number} easedProgress - Eased progress [0, 1]
 * @param {number} textLength - Total characters
 * @returns {object} TextTransformResult
 */
const computeSpread = (config, easedProgress, textLength) => {
  const distance = config.distance || 20;

  return {
    type: 'per-character',
    visibleCount: null,
    showCursor: false,
    transforms: null,
    letterSpacing: distance * (1 - easedProgress),
    filter: null,
  };
};

// ---------------------------------------------------------------------------
// Clarify
// ---------------------------------------------------------------------------

/**
 * Clarify: animate blur from blurAmount to 0.
 * Uses overall eased progress (uniform blur, not per-character).
 *
 * @param {object} config - { blurAmount: 10 }
 * @param {number} easedProgress - Eased progress [0, 1]
 * @returns {object} TextTransformResult
 */
const computeClarify = (config, easedProgress) => {
  const blurAmount = config.blurAmount || 10;
  const blur = blurAmount * (1 - easedProgress);

  return {
    type: 'full-text',
    visibleCount: null,
    showCursor: false,
    transforms: null,
    letterSpacing: null,
    filter: `blur(${blur.toFixed(2)}px)`,
  };
};

// ===========================================================================
// Category 1 entrance: per-glyph / per-unit reveals
// ===========================================================================

/**
 * Typewriter: reveal characters left-to-right at `cps` characters/second (default 20).
 * A blinking cursor can follow the last revealed char. Unlike `typing` (ms/char),
 * this uses characters-per-second to match the spec's default of ~20 cps.
 * config: { cps, showCursor }
 */
const computeTypewriter = (config, progress, duration, textLength) => {
  const cps = config.cps || 20; // characters per second
  const elapsedSec = progress * duration;
  const visibleCount = Math.min(textLength, Math.floor(elapsedSec * cps));
  return {
    type: 'full-text',
    visibleCount,
    showCursor: config.showCursor !== undefined ? config.showCursor : true,
    lastCharScale: 1,
    transforms: null,
    letterSpacing: null,
    filter: null,
  };
};

/**
 * Word-by-Word Reveal: reveal whole words in sequence, `wordDelay` seconds apart
 * (default 0.2s). Rendered as per-word visibility (consumers truncate by word).
 * config: { wordDelay }
 */
const computeWordReveal = (config, progress, duration, textLength) => {
  const wordDelay = config.wordDelay || 0.2;
  // We don't have the real word list here; estimate ~5 chars/word to size the count.
  const estWords = Math.max(1, Math.round(textLength / 5));
  const elapsedSec = progress * duration;
  const visibleCount = Math.min(estWords, Math.floor(elapsedSec / wordDelay) + (progress > 0 ? 1 : 0));
  return {
    type: 'per-word',
    visibleCount: Math.max(0, visibleCount),
    showCursor: false,
    transforms: null,
    letterSpacing: null,
    filter: null,
  };
};

/**
 * Letter-by-Letter Reveal: each character enters with its own staggered timing,
 * optionally rising / scaling / rotating in. Produces true per-character transforms.
 * config: { stagger (default 0.05s), distance (rise px, default 20), rotate (deg),
 *           startScale (default 1), direction:'up'|'down'|'left'|'right' }
 */
const computeLetterReveal = (config, progress, duration, textLength, easing) => {
  const stagger = config.stagger !== undefined ? config.stagger : 0.05;
  const distance = config.distance !== undefined ? config.distance : 20;
  const rotate = config.rotate || 0;
  const startScale = config.startScale !== undefined ? config.startScale : 1;
  const direction = config.direction || 'up';

  const transforms = buildCharTransforms(textLength, progress, duration, stagger, easing,
    (_up, ue) => {
      const rem = 1 - ue;
      let offsetX = 0, offsetY = 0;
      switch (direction) {
        case 'down': offsetY = -distance * rem; break;
        case 'left': offsetX = distance * rem; break;
        case 'right': offsetX = -distance * rem; break;
        case 'up':
        default: offsetY = distance * rem; break;
      }
      return {
        offsetX,
        offsetY,
        scale: startScale + (1 - startScale) * ue,
        rotation: rotate * rem,
        opacity: ue,
      };
    }
  );

  return {
    type: 'per-character',
    visibleCount: null,
    showCursor: false,
    transforms,
    letterSpacing: null,
    filter: null,
  };
};

// ===========================================================================
// Category 4 loop: per-character wave / letter-spacing loop
// ===========================================================================

/**
 * Wave: each character oscillates vertically (and optionally rotates) with a
 * phase shift based on its index, so a wave travels through the text. Loops
 * continuously. config: { amplitude (px, default 10), cycles (default 2),
 * charPhase (per-char phase in cycles, default 0.15), rotate (deg) }
 */
const computeWave = (config, progress, textLength) => {
  const amplitude = config.amplitude !== undefined ? config.amplitude : 10;
  const cycles = config.cycles !== undefined ? config.cycles : 2;
  const charPhase = config.charPhase !== undefined ? config.charPhase : 0.15;
  const rotate = config.rotate !== undefined ? config.rotate : 0;

  const transforms = new Array(textLength);
  for (let i = 0; i < textLength; i++) {
    const theta = 2 * Math.PI * (cycles * progress - i * charPhase);
    const s = Math.sin(theta);
    transforms[i] = {
      offsetX: 0,
      offsetY: amplitude * s,
      scale: 1,
      rotation: rotate * s,
      opacity: 1,
    };
  }
  return {
    type: 'per-character',
    visibleCount: null,
    showCursor: false,
    transforms,
    letterSpacing: null,
    filter: null,
  };
};

/**
 * Tracking Loop: letter spacing expands and contracts smoothly, looping.
 * config: { minTracking (px, default 0), maxTracking (px, default 12), cycles (default 2) }
 */
const computeTrackingLoop = (config, progress) => {
  const minTracking = config.minTracking !== undefined ? config.minTracking : 0;
  const maxTracking = config.maxTracking !== undefined ? config.maxTracking : 12;
  const cycles = config.cycles !== undefined ? config.cycles : 2;

  // (1 - cos)/2 oscillates 0->1->0 smoothly, continuous at the loop boundary.
  const wave = (1 - Math.cos(2 * Math.PI * cycles * progress)) / 2;
  const spacing = minTracking + (maxTracking - minTracking) * wave;
  return {
    type: 'full-text',
    visibleCount: null,
    showCursor: false,
    transforms: null,
    letterSpacing: spacing,
    filter: null,
  };
};

// ===========================================================================
// Category 5: Character & Word animation presets
//
// The first four are per-character (reuse the per-glyph render path). Word
// Highlight Cascade highlights whole words in sequence; it emits per-character
// transforms (scale) plus an optional per-char `color` the renderer applies to
// the active word's glyphs.
// ===========================================================================

/**
 * Staggered Rise: characters/words rise into place one after another.
 * config: { distance (px, 40), stagger (0.05), direction:'up'|'down'|'left'|'right', fade }
 */
const computeStaggeredRise = (config, progress, duration, textLength, easing) => {
  const distance = config.distance !== undefined ? config.distance : 40;
  const stagger = config.stagger !== undefined ? config.stagger : 0.05;
  const direction = config.direction || 'up';
  const fade = config.fade !== undefined ? config.fade : true;

  const transforms = buildCharTransforms(textLength, progress, duration, stagger, easing,
    (_up, ue) => {
      const rem = 1 - ue;
      let offsetX = 0, offsetY = 0;
      switch (direction) {
        case 'down': offsetY = -distance * rem; break;
        case 'left': offsetX = distance * rem; break;
        case 'right': offsetX = -distance * rem; break;
        case 'up':
        default: offsetY = distance * rem; break;
      }
      return { offsetX, offsetY, scale: 1, rotation: 0, opacity: fade ? ue : 1 };
    }
  );
  return { type: 'per-character', visibleCount: null, showCursor: false, transforms, letterSpacing: null, filter: null };
};

/**
 * Staggered Zoom: each character/word grows from `startScale` to 1 in sequence,
 * with optional overshoot and rotation. config: { startScale (0.2), stagger (0.05),
 * overshoot (0), rotate (deg) }
 */
const computeStaggeredZoom = (config, progress, duration, textLength, easing) => {
  const startScale = config.startScale !== undefined ? config.startScale : 0.2;
  const stagger = config.stagger !== undefined ? config.stagger : 0.05;
  const overshoot = config.overshoot !== undefined ? config.overshoot : 0;
  const rotate = config.rotate !== undefined ? config.rotate : 0;

  const transforms = buildCharTransforms(textLength, progress, duration, stagger, easing,
    (_up, ue) => {
      // Optional overshoot: peak slightly above 1 mid-way, settle to 1.
      const over = overshoot > 0 ? overshoot * Math.sin(Math.PI * ue) : 0;
      const scale = startScale + (1 - startScale) * ue + over;
      return { offsetX: 0, offsetY: 0, scale: Math.max(0, scale), rotation: rotate * (1 - ue), opacity: ue };
    }
  );
  return { type: 'per-character', visibleCount: null, showCursor: false, transforms, letterSpacing: null, filter: null };
};

/**
 * Character Rotation Cascade: each character rotates from `angle` to 0 in sequence.
 * config: { angle (deg, -90), stagger (0.05), fade }
 */
const computeCharRotationCascade = (config, progress, duration, textLength, easing) => {
  const angle = config.angle !== undefined ? config.angle : -90;
  const stagger = config.stagger !== undefined ? config.stagger : 0.05;
  const fade = config.fade !== undefined ? config.fade : true;

  const transforms = buildCharTransforms(textLength, progress, duration, stagger, easing,
    (_up, ue) => ({
      offsetX: 0, offsetY: 0, scale: 1,
      rotation: angle * (1 - ue),
      opacity: fade ? ue : 1,
    })
  );
  return { type: 'per-character', visibleCount: null, showCursor: false, transforms, letterSpacing: null, filter: null };
};

/**
 * Scatter and Assemble: characters start scattered (random offset/rotation/scale,
 * deterministic via seed) and converge to their final layout.
 * config: { distance (px, 120), stagger (0.03), rotate (deg, 90), seed (1) }
 */
const computeScatterAssemble = (config, progress, duration, textLength, easing) => {
  const distance = config.distance !== undefined ? config.distance : 120;
  const stagger = config.stagger !== undefined ? config.stagger : 0.03;
  const rotateMax = config.rotate !== undefined ? config.rotate : 90;
  const seed = config.seed !== undefined ? config.seed : 1;

  const transforms = buildCharTransforms(textLength, progress, duration, stagger, easing,
    (_up, ue, i) => {
      const rem = 1 - ue;
      const rx = seededNoise(i, seed);
      const ry = seededNoise(i, seed + 50);
      const rr = seededNoise(i, seed + 101);
      return {
        offsetX: distance * rx * rem,
        offsetY: distance * ry * rem,
        scale: 1,
        rotation: rotateMax * rr * rem,
        opacity: ue,
      };
    }
  );
  return { type: 'per-character', visibleCount: null, showCursor: false, transforms, letterSpacing: null, filter: null };
};

/**
 * Compute word boundaries from a string: returns an array mapping each char
 * index to its word index (whitespace chars map to -1). If `text` is missing,
 * falls back to estimating ~5 chars per word.
 */
const buildWordIndexMap = (text, textLength) => {
  if (typeof text === 'string' && text.length > 0) {
    const map = new Array(text.length);
    let word = -1;
    let inWord = false;
    for (let i = 0; i < text.length; i++) {
      const ch = text[i];
      const isSpace = ch === ' ' || ch === '\n' || ch === '\t';
      if (isSpace) {
        map[i] = -1;
        inWord = false;
      } else {
        if (!inWord) { word++; inWord = true; }
        map[i] = word;
      }
    }
    return { map, wordCount: word + 1 };
  }
  // Fallback estimate.
  const avg = 5;
  const map = new Array(textLength);
  for (let i = 0; i < textLength; i++) map[i] = Math.floor(i / avg);
  return { map, wordCount: Math.max(1, Math.ceil(textLength / avg)) };
};

/**
 * Word Highlight Cascade: highlight words one at a time (scale up + recolor).
 * The active word is derived from progress. Previous words optionally stay
 * highlighted. config: { highlightColor ('#ffd400'), scale (1.15), persist:boolean,
 * holdColor (base color when not highlighted, optional) }
 */
const computeWordHighlightCascade = (config, progress, duration, textLength, easing, text) => {
  const highlightColor = config.highlightColor || '#ffd400';
  const hiScale = config.scale !== undefined ? config.scale : 1.15;
  const persist = config.persist !== undefined ? config.persist : false;

  const { map, wordCount } = buildWordIndexMap(text, textLength);
  const ep = getEasingValue(progress, easing);
  // Active word index advances across the words over the window.
  const activeWord = Math.min(wordCount - 1, Math.floor(ep * wordCount));
  // Fractional progress within the active word's slot (for a smooth scale pop).
  const slot = 1 / Math.max(1, wordCount);
  const local = Math.max(0, Math.min(1, (ep - activeWord * slot) / slot));
  const pop = Math.sin(Math.PI * local); // 0 -> 1 -> 0 within the slot

  const n = map.length;
  const transforms = new Array(n);
  for (let i = 0; i < n; i++) {
    const w = map[i];
    const isActive = w === activeWord;
    const isPast = persist && w >= 0 && w < activeWord;
    const highlighted = isActive || isPast;
    const scale = isActive ? (1 + (hiScale - 1) * pop) : (isPast ? hiScale : 1);
    transforms[i] = {
      offsetX: 0, offsetY: 0,
      scale,
      rotation: 0,
      opacity: 1,
      color: highlighted ? highlightColor : null,
    };
  }
  return { type: 'per-character', visibleCount: null, showCursor: false, transforms, letterSpacing: null, filter: null };
};

// ===========================================================================
// Category 6: Advanced reveals
//
// trackingReveal + charFlipCascade are per-character. The mask-based reveals
// (curtain/liquid/drawOn/behindObject) emit a `reveal` descriptor the renderer
// turns into a clip/alpha mask:
//   reveal = { mode, progress, direction, softness, wave }
//
// NOTE on fidelity:
//  - textDrawOn is a true stroke-following effect only with vector outlines,
//    which raster text doesn't provide. We approximate with a directional wipe.
//  - textBehindObject needs a foreground segmentation matte (not available
//    here). We approximate with a directional reveal (as if emerging from an
//    edge). Supply a real matte upstream for true occlusion.
// ===========================================================================

/**
 * Tracking Reveal: letters start widely spaced + faded and converge to the
 * final spacing while fading in. Per-character (offsetX from center + opacity).
 * config: { tracking (extra px/char at start, 24), stagger (0.03) }
 */
const computeTrackingReveal = (config, progress, duration, textLength, easing) => {
  const tracking = config.tracking !== undefined ? config.tracking : 24;
  const stagger = config.stagger !== undefined ? config.stagger : 0.03;
  const mid = (textLength - 1) / 2;

  const transforms = buildCharTransforms(textLength, progress, duration, stagger, easing,
    (_up, ue, i) => {
      const rem = 1 - ue;
      // Spread chars out from the center line at the start, converge by the end.
      const offsetX = (i - mid) * tracking * rem;
      return { offsetX, offsetY: 0, scale: 1, rotation: 0, opacity: ue };
    }
  );
  return { type: 'per-character', visibleCount: null, showCursor: false, transforms, letterSpacing: null, filter: null };
};

/**
 * Character Flip Cascade: each character flips in around its own axis (3D),
 * staggered. config: { axis:'x'|'y', startAngle (90), stagger (0.05), fade }
 * Emits per-char rotationX/rotationY which the renderer applies as a flip.
 */
const computeCharFlipCascade = (config, progress, duration, textLength, easing) => {
  const axis = config.axis || 'y';
  const startAngle = config.startAngle !== undefined ? config.startAngle : 90;
  const stagger = config.stagger !== undefined ? config.stagger : 0.05;
  const fade = config.fade !== undefined ? config.fade : true;

  const transforms = buildCharTransforms(textLength, progress, duration, stagger, easing,
    (_up, ue) => {
      const angle = startAngle * (1 - ue);
      return {
        offsetX: 0, offsetY: 0, scale: 1, rotation: 0,
        rotationX: axis === 'x' ? angle : 0,
        rotationY: axis === 'y' ? angle : 0,
        opacity: fade ? Math.min(1, ue * 2) : 1,
      };
    }
  );
  return { type: 'per-character', visibleCount: null, showCursor: false, transforms, letterSpacing: null, filter: null };
};

/**
 * Curtain Reveal: two masks open from the center (or an edge) exposing the
 * stationary text. config: { direction:'horizontal'|'vertical', softness (0-0.3), reverse }
 */
const computeCurtainReveal = (config, ep) => {
  const direction = config.direction || 'horizontal';
  const softness = config.softness !== undefined ? config.softness : 0.05;
  const p = config.reverse ? (1 - ep) : ep;
  return {
    type: 'full-text', visibleCount: null, showCursor: false, transforms: null,
    letterSpacing: null, filter: null,
    reveal: { mode: 'curtain', progress: p, direction, softness },
  };
};

/**
 * Liquid Reveal: directional reveal with a wavy (liquid) boundary. config:
 * { direction:'left'|'right'|'up'|'down', softness (0.08), wave (amplitude 0-0.2),
 *   frequency (ripples, 3) }
 */
const computeLiquidReveal = (config, ep) => {
  const direction = config.direction || 'left';
  const softness = config.softness !== undefined ? config.softness : 0.08;
  const wave = config.wave !== undefined ? config.wave : 0.08;
  const frequency = config.frequency !== undefined ? config.frequency : 3;
  return {
    type: 'full-text', visibleCount: null, showCursor: false, transforms: null,
    letterSpacing: null, filter: null,
    reveal: { mode: 'liquid', progress: ep, direction, softness, wave, frequency },
  };
};

/**
 * Text Draw-On: approximated as a crisp directional wipe (true stroke-follow
 * needs vector outlines). config: { direction:'left'|'right'|'up'|'down', softness }
 */
const computeTextDrawOn = (config, ep) => {
  const direction = config.direction || 'left';
  const softness = config.softness !== undefined ? config.softness : 0.02;
  return {
    type: 'full-text', visibleCount: null, showCursor: false, transforms: null,
    letterSpacing: null, filter: null,
    reveal: { mode: 'wipe', progress: ep, direction, softness },
  };
};

/**
 * Text Behind Object: approximated as a directional reveal from an edge (as if
 * emerging from behind something). True occlusion needs a foreground matte.
 * config: { direction:'up'|'down'|'left'|'right', softness }
 */
const computeTextBehindObject = (config, ep) => {
  const direction = config.direction || 'up';
  const softness = config.softness !== undefined ? config.softness : 0.04;
  return {
    type: 'full-text', visibleCount: null, showCursor: false, transforms: null,
    letterSpacing: null, filter: null,
    reveal: { mode: 'wipe', progress: ep, direction, softness },
  };
};
